import { Plugin, Editor, Collection, createDropdown, addListToDropdown } from "ckeditor5";
import type { ListDropdownItemDefinition, ListDropdownButtonDefinition } from "ckeditor5";
import { Operation, operationToCommand } from "./responseTransformationPluginUtil";
import { EXPAND_WITH_CONTEXT } from "./expandResponseWithContextCommand";
import { SUMMARIZE_RESPONSE } from "./summarizeResponseCommand";
// @ts-ignore
import i18next from "i18n/i18n";

export default class ResponseTransformationUI extends Plugin {
  public init(): void {
    const editor: Editor = this.editor;
    const operations = editor.config.get("ai.operations") as Operation[];
    const skipExpandWithContext = editor.config.get("skipExpandWithContext");
    const skipResponseTransform = editor.config.get("skipResponseTransform");
    this._registerAiDropdown(editor, operations, !skipExpandWithContext, !skipResponseTransform);
  }

  private _registerAiDropdown(
    editor: Editor,
    operations: Operation[],
    includeExpandWithContext: boolean,
    includeResponseTransform: boolean,
  ): void {
    editor.ui.componentFactory.add("ai", (locale) => {
      const dropdownView = createDropdown(locale);
      const accessibleLabel = i18next.t("ai.menu.label");

      const listOptions: ListDropdownItemDefinition[] = this._prepareListOptions(
        operations,
        includeExpandWithContext,
        includeResponseTransform,
      );

      const itemDefinitions = new Collection<ListDropdownItemDefinition>();
      itemDefinitions.addMany(listOptions);

      addListToDropdown(dropdownView, itemDefinitions, {
        role: "menu",
        ariaLabel: accessibleLabel,
      });

      dropdownView.buttonView.set({
        label: accessibleLabel,
        tooltip: true,
        withText: true,
      });

      this.listenTo(dropdownView, "execute", (evt: any) => {
        const { commandName } = evt.source;
        if (commandName) {
          editor.execute(commandName);
        }
      });

      return dropdownView;
    });
  }

  private _prepareListOptions(
    operations: Operation[],
    includeExpandWithContext: boolean,
    includeResponseTransform: boolean,
  ): ListDropdownButtonDefinition[] {
    const items = [];
    if (includeResponseTransform) {
      items.push(
        ...operations.map(
          (op) =>
            ({
              type: "button" as const,
              model: {
                commandName: operationToCommand(op.name),
                label: this._getOperationLabel(op),
                withText: true,
              },
            }) as unknown as ListDropdownButtonDefinition,
        ),
      );
    }

    if (includeExpandWithContext) {
      items.push({
        type: "button" as const,
        model: {
          commandName: EXPAND_WITH_CONTEXT,
          label: i18next.t("ai.operation.expandWithContext"),
          withText: true,
        },
      } as unknown as ListDropdownButtonDefinition);
    }

    if (includeResponseTransform) {
      items.push({
        type: "button" as const,
        model: {
          commandName: SUMMARIZE_RESPONSE,
          label: i18next.t("ai.operation.summarizeResponse"),
          withText: true,
        },
      } as unknown as ListDropdownButtonDefinition);
    }

    return items;
  }

  private _getOperationLabel(op: Operation): string {
    // Try to resolve translation key first to support multiple languages
    let label = i18next.t(`ai.operation.${op.name}`);
    // If no translation is found, fallback to API-provided displayName
    if (label === `ai.operation.${op.name}`) {
      label = op.displayName;
    }
    return label;
  }

  public static get pluginName(): String {
    return "ResponseTransformationUI";
  }
}
